<template>
  <div class="medications-on-at-start-panel" data-testid="medications-on-at-start-container">
    <help-tooltip v-if="bagCountError" type="error" class="tooltip" aria-label="Bag Count Error" data-testid="bag-count-error">
      {{ bagCountError.message }}
    </help-tooltip>
    <div class="tabs-container" data-testid="bag-tabs-container">
      <vrp-tabs
        :tabs="tabs"
        :active-tab-key="state.activeTabKey"
        @change-tab="setTabKey"
      >
        <template v-slot:header-prefix>
          <md-button
            class="md-secondary-accent md-raised rounded new-initial-medication-button"
            :disabled="addBagDisabled || readonly"
            aria-label="Add Bag"
            data-testid="add-bag-button"
            @click="startNewInitialMedication"
          >
            <md-icon>add</md-icon>
            Bag
          </md-button>
        </template>
        <template #item="{ initialMedication }">
          <initial-medication
            :initial-medication="initialMedication"
            :parent-bag="getParentBag(initialMedication)"
            :case-conf="caseConf"
            :readonly="readonly"
            :medications="medications"
            @update="
              updatedInitialMedication =>
                updateInitialMedication(updatedInitialMedication)
            "
          />
        </template>
      </vrp-tabs>
    </div>
    <md-button
      v-if="!removeDisabled"
      class="md-raised md-accent remove-button"
      :disabled="readonly"
      aria-label="Remove Bag"
      data-testid="remove-bag-button"
      @click="removeInitialMedication"
    >
      Remove
    </md-button>
  </div>
</template>

<script>
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import Chance from 'chance';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import VrpTabs from '../../../common/VrpTabs/VrpTabs';
import InitialMedication from './InitialMedication.vue';
import { initialMedicationLabel } from './initialMedicationLabel';

const chance = new Chance();

export default {
  name: 'medications-on-at-start-panel',
  components: {
    InitialMedication,
    VrpTabs,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    medications: {
      type: Array,
      required: true,
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      state: {
        pendingInitialMedication:
          this.caseConf.initialMedications &&
          this.caseConf.initialMedications.length
            ? null
            : {
                id: chance.guid(),
                medOrFluidId: null,
                volume: null,
              },
        activeTabKey: this.initialActiveId(),
        isLoading: false,
      },
    };
  },
  computed: {
    allMedicationsById() {
      const allMeds = this.medications.concat(this.caseConf.customMedications || []);
      return keyBy(allMeds, 'id');
    },
    activeInitialMedication() {
      if (this.state.activeTabKey === 'pending') {
        return this.state.pendingInitialMedication;
      }
      const index = this.caseConf.initialMedications.findIndex(im => im.id === this.state.activeTabKey);
      return this.caseConf.initialMedications[index];
    },
    addBagDisabled() {
      const isPending = !!this.state.pendingInitialMedication;
      const activeInvalid = this.isValid(this.activeInitialMedication);
      const numBagsAllowed = this.caseConf.iv.bagCount;
      const numBags = (this.caseConf.initialMedications || []).length;
      return isPending || !activeInvalid || numBags >= numBagsAllowed;
    },
    removeDisabled() {
      const numBags = (this.caseConf.initialMedications || []).length;
      const initMed = this.activeInitialMedication;
      const isParentBag = this.isParentBag(initMed);
      return numBags === 0 || isParentBag;
    },
    tabs() {
      const tabs = (this.caseConf.initialMedications || []).map(initMed => {
        return {
          key: initMed.id,
          label: this.initialMedicationLabel(initMed),
          initialMedication: initMed,
        };
      });
      if (this.state.pendingInitialMedication) {
        tabs.push({
          key: 'pending',
          label: this.state.pendingInitialMedication
              ? this.initialMedicationLabel(this.state.pendingInitialMedication)
              : '',
          initialMedication: this.state.pendingInitialMedication
        });
      }
      return tabs;
    },
    initialMedErrors() {
      return this.$store
        ? get(
          this.$store.getters['validation/errorsByField'],
          'initialMedications'
        ) || [] : [];
    },
    bagCountError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'bagCount');
    },
  },
  created() {
    if (this.navigation.params && this.navigation.params.field === 'initialMedications') {
      this.setTabKey(this.navigation.params.id);
    }
  },
  methods: {
    setTabKey(key) {
      this.state.activeTabKey = key;
    },
    initialActiveId() {
      if (this.caseConf.initialMedications && this.caseConf.initialMedications.length) {
        return this.caseConf.initialMedications[0].id;
      }
      return 'pending';
    },
    initialMedicationLabel(initialMedication) {
      if (!initialMedication.medOrFluidId) {
        const index = (this.caseConf.initialMedications || []).indexOf(
          initialMedication,
        );
        const initialMedicationsCount = (this.caseConf.initialMedications || [])
          .length;
        return `Bag ${index !== -1 ? index + 1 : initialMedicationsCount + 1}`;
      }
      return initialMedicationLabel(
        initialMedication,
        this.allMedicationsById,
        this.caseConf,
      );
    },
    startNewInitialMedication() {
      this.state.pendingInitialMedication = {
        id: chance.guid(),
        medOrFluidId: null,
        volume: null,
      };
      this.state.activeTabKey = 'pending';
    },
    updateInitialMedication(updatedInitialMedication) {
      if (this.state.isLoading) {
        return;
      }

      let initMeds = this.caseConf.initialMedications || [];
      const index = initMeds.findIndex(im => im.id === updatedInitialMedication.id);

      if (this.state.activeTabKey === 'pending') {
        this.state.pendingInitialMedication = updatedInitialMedication;
        // Add the pending to the case
        const { pendingInitialMedication } = this.state;
        if (this.canSave(pendingInitialMedication)) {
          initMeds = initMeds.concat(pendingInitialMedication);
          this.state.isLoading = true;
          this.$nextTick(() => {
            this.state.activeTabKey = updatedInitialMedication.id;
            this.state.pendingInitialMedication = null;
            this.state.isLoading = false;
          });
        }
      } else {
        initMeds[index] = updatedInitialMedication;
      }

      this.$emit('update', initMeds);
    },
    removeInitialMedication() {
      const index = this.caseConf.initialMedications.findIndex(
        im => im.id === this.activeInitialMedication.id
      );
      if (index >= 0) {
        this.caseConf.initialMedications.splice(index, 1);
        if (this.caseConf.initialMedications.length > 0) {
          this.setTabKey(this.caseConf.initialMedications[0].id);
        } else {
          this.startNewInitialMedication();
        }
        this.$emit('update', this.caseConf.initialMedications);
      }
    },
    isValid(initialMed) {
      const medValid = initialMed.medOrFluidId && initialMed.volume
      const dripSetValid = initialMed.isDropSetEnabled && initialMed.rate && initialMed.dripSet;
      const primaryPump = initialMed.ivPumpPrimary;
      const pumpValid = primaryPump && primaryPump.pumpFunction && primaryPump.rate &&
        (primaryPump.hours || primaryPump.minutes);
      const connection = initialMed.connection;
      const connectionValid = connection &&
        ((connection.type === 'IV_SITE' && connection.port) || connection.type === 'MEDICATION_OR_FLUID');
      return medValid && (dripSetValid || pumpValid) && connectionValid;
    },
    isParentBag(initialMed) {
      let isParent = false;
      for (const otherInitMed of this.caseConf.initialMedications || []) {
        if (otherInitMed.id !== initialMed.id) {
          if (otherInitMed.connection && otherInitMed.connection.type === 'MEDICATION_OR_FLUID' && otherInitMed.connection.id === initialMed.id) {
            isParent = true;
          }
        }
      }
      return isParent;
    },
    getParentBag(initialMed) {
      if (initialMed.connection && initialMed.connection.type === 'MEDICATION_OR_FLUID') {
        return this.caseConf.initialMedications.find(
          otherInitMed => otherInitMed.id === initialMed.connection.id
        );
      }
    },
    canSave(initialMed) {
      return initialMed.medOrFluidId;
    }
  },
};
</script>

<style scoped>
.tooltip {
  margin-top: 8px;
}

.tabs-container {
  display: flex;
  align-items: center;
  padding-top: 8px;
}

.new-initial-medication-button {
  margin: 0;
  margin-right: 15px;
  width: 100px;
}

.new-initial-medication-button .md-icon {
  color: white;
}

.vrp-tabs {
  flex: 1;
}
</style>
